import { useEffect } from 'react';
import { useConsentStore } from '@/lib/consent/store';

/**
 * Google Consent Mode v2 at run time (D-357). The page's `<head>` already says "everything denied" — and, for a
 * visitor whose accepted choice is stored, "analytics granted" (the inline script of `./snippet.ts`, before the
 * container script runs). What is left for the app is the CHANGE: a visitor who presses Accept in the cookie bar,
 * and one who takes the choice back ("Cookie preferences", or Reject).
 *
 *   Accept   → `consent update { analytics_storage: granted }`; advertising storage, advertising data and
 *              personalisation are never granted — the notice does not offer them.
 *   Withdraw → `consent update { analytics_storage: denied }` and the `_ga*` cookies Google's tags wrote are
 *              deleted (the cookie code is a lazy chunk, loaded only then).
 *
 * Does nothing in a build without `VITE_GTM_ID` (the dev server, the end-to-end build): there is no `dataLayer`
 * to talk to. Small on purpose — it is part of the first load (`entry-bundle.test.ts`, `budgets.json`).
 */

type CommandQueue = (...args: unknown[]) => void;

/** Whatever carries the `dataLayer` array: the window. */
interface DataLayerHolder {
  dataLayer?: unknown[];
}

/** The window's `dataLayer`, created if the snippet has not (a build without one never calls this). */
export function updateConsent(
  granted: boolean,
  target: DataLayerHolder = window as unknown as DataLayerHolder,
): void {
  const layer = (target.dataLayer ??= []);
  // Google's tags only understand the `Arguments` object as a command (their own snippet is
  // `function gtag(){dataLayer.push(arguments)}`), never an array.
  const command: CommandQueue = function () {
    // eslint-disable-next-line prefer-rest-params -- see above
    layer.push(arguments);
  };
  command('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });
}

/** Google Tag Manager is part of this build (`VITE_GTM_ID`, read by the build from `apps/web/.env.production`). */
const isOn = (): boolean => Boolean(import.meta.env.VITE_GTM_ID?.trim());

/** Follow the consent store for the life of the page. Returns the unsubscribe function. */
export function startGtmConsent(): () => void {
  if (!isOn()) return () => undefined;
  return useConsentStore.subscribe((state, previous) => {
    const granted = state.decision === 'accepted';
    const wasGranted = previous.decision === 'accepted';
    if (granted === wasGranted) return;
    updateConsent(granted);
    if (!granted) {
      // the cookies Google's tags wrote while the visitor had accepted
      void import('@/lib/analytics/providers/ga4').then(
        (module) => {
          module.removeGaCookies(document, window.location.hostname);
        },
        () => undefined,
      );
    }
  });
}

/** Mount once, in the providers. */
export function useGtmConsentBootstrap(): void {
  useEffect(() => startGtmConsent(), []);
}
